/**
 * Public catalogue copied from sgapp-client/src/pages/preview at a19204d8.
 * Only Project 180 is public; the other entries are staff-only placeholders.
 */
export const PREVIEW_INTRO = {
  eyebrow: '240 Labs',
  headline: 'The classroom is where the real work starts.',
  body: "We’ve helped more than half a million teachers pass their certification exams. Then you get your own classroom, and the hard part is everything the job piles on after the bell. 240 Labs is where we build for every step of your career as a teacher.",
};

export const PROJECT_180 = {
  id: 'project-180',
  name: 'Project 180',
  tagline: 'Your first 180 days',
  summary: 'Helping new teachers think, act and teach like 20-year veterans.',
  lede: "Project 180 gives first-year teachers the frameworks and classroom tools veteran teachers took years to develop. It's built on the practices of state Teachers of the Year and decades of classroom research, and organized around your first 180 days — so the right support shows up exactly when you need it.",
  beatsHeading: 'The Three Bones Framework',
  beats: [
    { title: 'Wishbone', body: 'Your mindset. Your belief in students sets their ceiling.' },
    { title: 'Backbone', body: 'Your classroom management. Students follow teachers they trust.' },
    { title: 'Funnybone', body: 'Your instruction. Structure creates learning — feedback keeps it going.' },
  ],
  learnMoreUrl: 'https://www.240.org/project-180/',
  notifyUrl: 'https://study.240tutoring.com/preview',
};

/**
 * A notify-me card with nothing behind it yet. The idea: teachers who have done the job
 * set up shop and share what worked, held to a standard, with real classrooms behind
 * the claims. Listed behind a gate that fails closed until there is something to open.
 */
export const MARKETPLACE = {
  id: 'marketplace',
  name: '240 Marketplace',
  tagline: 'Made by teachers who have done the job',
  summary: 'Ready-to-use products from teachers who have been in the field.',
  lede: "A place where experienced teachers set up shop and share what actually worked: lessons, materials and tools, held to a high standard and ready to teach from. Find work from people who have stood where you stand, or share your own.",
  beatsHeading: 'What makes the cut',
  beats: [
    { title: 'Built by teachers', body: 'Every product comes from someone who has taught it, not someone who guessed at it.' },
    { title: 'Held to a standard', body: 'Listings are reviewed before they go up. A high bar is the point.' },
    { title: 'Ready to use', body: 'Complete enough to teach from Monday, not a sketch you have to finish.' },
  ],
  /**
   * The sample covers on the editorial art. Illustrative, not listings: each title has
   * to be something a teacher of that grade and subject would actually go looking for.
   */
  covers: [
    { level: 'Grade 3 · Science', title: 'Plant Life Cycles', detail: '2-week unit' },
    { level: 'Grades 6–8 · SEL', title: 'Restorative Circles', detail: '20 circle scripts' },
    { level: 'Grades 9–12 · Math', title: 'Linear Functions Stations', detail: '6 stations + keys' },
  ],
};

/** Account-backed subscription to news about Labs as a whole. */
export const PREVIEW_UPDATES = { id: 'preview-updates', name: 'Labs updates' } as const;

/**
 * 240 Collab. Listed only when `preview-collab-visible` is explicitly true — a gate that
 * fails closed, like Objectives and Sites, and one that decides listing and nothing else.
 * Whether a teacher may use Collab is Collab's own business: it authenticates her itself.
 */
export const COLLAB = {
  id: 'collab',
  name: '240 Collab',
  tagline: 'Put your colleagues around your own materials.',
  summary: 'Make a private group, invite the colleagues you trust, and work on your materials together.',
  lede: 'Share a lesson, a unit, a handout — anything you made — into a group only the people you invited can see. They read it, comment on it, and answer each other. Nothing is public, nothing is listed, and an invitation only works for the address it was sent to.',
  beatsHeading: 'How a group works',
  beats: [
    { title: 'Invite by name', body: 'A link bound to one address. Forwarding it does not admit anyone else.' },
    { title: 'Share what you made', body: 'Your own materials, in one place, visible to the group and nobody else.' },
    { title: 'Talk about the work', body: 'Comments and replies sit with the material, so the thread stays where the file is.' },
  ],
} as const;

/**
 * 240 Planner. Listed only when `preview-planner-visible` is explicitly true — a gate that
 * fails closed and decides listing and nothing else. The Planner's Worker reads its own
 * kill switch, `preview-planner-disabled`, which fails open.
 */
export const PLANNER = {
  id: 'planner',
  name: '240 Planner',
  tagline: 'Your course’s standards, laid across your school year.',
  summary: 'Lay your course’s official standards across your school calendar as units. Open it each week to see what you teach now and next, with the 240 topics that cover it.',
} as const;

/**
 * 240 Profile. Listed only when `preview-profile-visible` is explicitly true — a gate that
 * fails closed and decides listing and nothing else. Who may build a profile is decided by
 * the Profile Worker (staff only while R18 holds), which also reads its own kill switch,
 * `preview-profile-disabled`, failing open.
 */
export const PROFILE = {
  id: 'profile',
  name: '240 Profile',
  tagline: 'Your teaching career on one page you control.',
  summary: 'One page for your teaching career: photo, certifications, jobs, and awards. Texas and Florida certificates show a badge once 240 checks them against the state. Share it by link.',
} as const;

/**
 * AI Detector. Listed only when `labs-ai-detector-visible` is explicitly true — a gate that
 * fails closed and decides listing and nothing else. Who may run a check is decided by the
 * AI Detector Worker (240 staff emails plus `labs-ai-detector-access`), which also reads its
 * own kill switch, `labs-ai-detector-disabled`, failing open.
 */
export const AI_DETECTOR = {
  id: 'ai_detector',
  name: 'AI Detector',
  tagline: 'See which passages read as AI-written.',
  summary: 'Paste a piece of writing and see which passages Pangram judges AI-generated, AI-assisted, or human, highlighted in place.',
} as const;

export const ONE_STEP_AHEAD = {
  id: 'one-step-ahead',
  name: 'One Step Ahead',
  tagline: 'Know the material before your students do.',
  steps: [
    { title: 'Say what you have to teach', detail: 'A unit name, a chapter, a line from your department head.' },
    { title: 'See the state standards', detail: 'One row for each standard in it.' },
    { title: 'Read the 240 pages', detail: 'The study-guide content that covers each one, right there.' },
  ],
  summary: "Handed a subject or unit you haven't taught before? Describe the assignment and choose your state to find the official standards and matching 240 topics. Read each topic here, with the standards themselves shown wherever we don't yet have content.",
};
